/**
 * Analysis-order API — mirrors smt_order_handler.go.
 *
 * User endpoints:
 *   POST   /api/v1/smt/orders                Create
 *   GET    /api/v1/smt/orders                ListMine
 *   DELETE /api/v1/smt/orders/:id            Cancel
 *
 * Admin endpoints (require admin JWT):
 *   GET    /api/v1/smt/admin/orders          AdminList
 *   PUT    /api/v1/smt/admin/orders/:id/approve
 *   PUT    /api/v1/smt/admin/orders/:id/reject
 */
import { request, requestData } from './client.ts';

export type OrderStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface Order {
  id: number;
  user_id: number;
  estate_id: number;
  status: OrderStatus;
  message?: string | null;
  decision_note?: string | null;
  created_at: string;
  decided_at?: string | null;
  decided_by?: number | null;
  user_email?: string | null;
  user_name?: string | null;
  estate_name?: string | null;
  estate_region?: string | null;
  municipality?: string | null;
}

export interface CreateOrderInput {
  estate_id: number;
  message?: string;
}

export async function createOrder(input: CreateOrderInput): Promise<Order> {
  return requestData<Order>('/smt/orders', { method: 'POST', body: input });
}

export async function listMyOrders(): Promise<Order[]> {
  return (await requestData<Order[] | null>('/smt/orders')) ?? [];
}

export async function cancelOrder(id: number): Promise<void> {
  await request<unknown>(`/smt/orders/${id}`, { method: 'DELETE' });
}

// --- Admin ---

export interface AdminListOrdersOptions {
  status?: OrderStatus | 'all';
}

export async function adminListOrders(opts: AdminListOrdersOptions = {}): Promise<Order[]> {
  const query: Record<string, string> = {};
  if (opts.status && opts.status !== 'all') query.status = opts.status;
  return (await requestData<Order[] | null>('/smt/admin/orders', { query })) ?? [];
}

export async function adminApproveOrder(id: number, note?: string): Promise<Order> {
  return requestData<Order>(`/smt/admin/orders/${id}/approve`, {
    method: 'PUT',
    body: { decision_note: note || '' },
  });
}

export async function adminRejectOrder(id: number, note?: string): Promise<Order> {
  return requestData<Order>(`/smt/admin/orders/${id}/reject`, {
    method: 'PUT',
    body: { decision_note: note || '' },
  });
}
